"use client";

import { sponsor } from "@/features/sponsor";
import {
  ProductCard,
  ProductCardBody,
  ProductCardBrand,
  ProductCardFreeShippingTag,
  ProductCardFullTag,
  ProductCardName,
  ProductCardNewTag,
  ProductCardSponsoredTag,
  ProductPriceBlock,
} from "@/shared";
import { useViewportTracking } from "@dafiti/analytics-sdk";
import { cn } from "@dafiti/closet-ui/utils";
import type { ReactNode } from "react";

import * as track from "../../../tracking/catalogPage.tracking";
import type {
  CatalogMetaResponse,
  CatalogProduct,
} from "../../../types/catalog.type";
import { CatalogProductCardImage } from "./CatalogProductCardImage";

export const CatalogProductCard = ({
  product,
  index,
  meta,
  imageOverlaySlot,
  disableTracking = false,
  disableViewportTracking = false,
  cardClassName,
}: {
  product: CatalogProduct;
  index?: number;
  meta?: CatalogMetaResponse;
  imageOverlaySlot?: ReactNode;
  disableTracking?: boolean;
  disableViewportTracking?: boolean;
  cardClassName?: string;
}) => {
  const { ref } = useViewportTracking(
    product.sku,
    { ...product, index },
    {
      threshold: 0.5,
    },
  );

  const showSponsoredTag =
    sponsor.isSponsoredAdsEnabled() && product.sponsor?.is_sponsored;

  return (
    <div ref={disableViewportTracking ? undefined : ref}>
      <ProductCard
        className={cn("w-full cursor-pointer group", cardClassName)}
        data-testid="catalog-product-card"
        onClick={
          disableTracking
            ? undefined
            : () =>
                track.catalogProductClick({ product, index, catalogMeta: meta })
        }
      >
        <CatalogProductCardImage
          product={product}
          productIndex={index}
          imageOverlaySlot={imageOverlaySlot}
        />

        <ProductCardBody>
          <div className="mt-1 mb-1 flex justify-between items-center">
            <ProductCardBrand
              className="xl:text-2xs"
              data-testid="product-card-brand"
            >
              {product.brand}
            </ProductCardBrand>
            {product.tags?.is_new && (
              <ProductCardNewTag
                className="xl:text-2xs"
                data-testid="product-card-new-tag"
              />
            )}
          </div>

          <ProductCardName
            className="line-clamp-2 xl:text-body-2"
            data-testid="product-card-name"
          >
            {product.name}
          </ProductCardName>

          <ProductPriceBlock price={product.price} className="mt-1" />

          {(product.tags?.is_full_delivery ||
            product.tags?.is_free_shipping) && (
            <div className="mt-1 flex items-center gap-1">
              {product.tags?.is_free_shipping && (
                <ProductCardFreeShippingTag data-testid="product-card-free-shipping-tag" />
              )}
              {product.tags?.is_full_delivery && (
                <ProductCardFullTag data-testid="product-card-full-tag" />
              )}
            </div>
          )}

          {showSponsoredTag && (
            <ProductCardSponsoredTag
              className="lg:text-2xs mt-1"
              data-testid="product-card-sponsored-tag"
            />
          )}
        </ProductCardBody>
      </ProductCard>
    </div>
  );
};
